import type { ApplyState } from '../write/writeTypes';

/**
 * Basil v0.2, P4 — every outcome of an apply, named with its actual count.
 *
 * Four laws, and all four are the same law seen from different sides.
 *
 * 1. **Success wording only after confirmation.** "Applied and checked" counts the requests the
 *    verifier confirmed and nothing else. A request that was sent, a request whose reply was lost
 *    and a request that never left are all not that.
 * 2. **A mixed result is never collapsed.** Three facts get three clauses with three real numbers.
 *    There is no arrangement of counts in which a partial result reads as a whole one.
 * 3. **"The change was sent" only when the send is known.** An unobserved outcome claims neither
 *    delivery nor its absence, because neither is true.
 * 4. **Nothing here is rounded, inferred or softened.** The counts come from the pipeline's own
 *    per-request statuses.
 */

/** How many requests ended in each of the four places. */
export interface ApplyOutcome {
  /** verified by the double-check: the only state that is a success. */
  confirmed: number;
  /** the engine had it and the result could not be confirmed. */
  unconfirmedSent: number;
  /** dispatched, reply lost: it may be in the episode and may not be. */
  unconfirmedUnknown: number;
  /** nothing left the browser for this one. */
  notSent: number;
  /** every request the apply knew about. */
  total: number;
}

const EMPTY: ApplyOutcome = Object.freeze({
  confirmed: 0,
  unconfirmedSent: 0,
  unconfirmedUnknown: 0,
  notSent: 0,
  total: 0,
});

/** Count where each request of an apply ended up. */
export function outcomeOf(state: ApplyState | null | undefined): ApplyOutcome {
  const statuses = state ? state.statuses : {};
  const planIds = Object.keys(statuses);

  if (planIds.length === 0) {
    return EMPTY;
  }
  const counted = { ...EMPTY, total: planIds.length };

  planIds.forEach((planId) => {
    const entry = statuses[planId];

    if (entry.status === 'verified') {
      counted.confirmed += 1;

      return;
    }
    if (entry.delivery === 'sent') {
      counted.unconfirmedSent += 1;

      return;
    }
    if (entry.delivery === 'unknown') {
      counted.unconfirmedUnknown += 1;

      return;
    }
    counted.notSent += 1;
  });

  return counted;
}

const changes = (count: number): string => `${count} change${count === 1 ? '' : 's'}`;

const wasWere = (count: number): string => (count === 1 ? 'was' : 'were');

/** The confirmed clause. It is the ONLY place the word "checked" is allowed to appear. */
export function confirmedClause(count: number): string {
  return `${changes(count)} applied and checked.`;
}

/** The clause for everything the double-check could not settle, however it got there. */
export function unconfirmedClause(count: number): string {
  return `${changes(count)} could not be confirmed.`;
}

/** The clause for the requests that never left. */
export function notSentClause(count: number): string {
  return `${changes(count)} ${wasWere(count)} not sent.`;
}

/**
 * The whole outcome as one line, with actual counts and states.
 *
 * A mixed result reads "2 changes applied and checked. 1 change could not be confirmed. 1 change
 * was not sent." — three clauses, because there are three facts. A result with one kind in it reads
 * as that one clause. An apply with no requests at all has nothing to say.
 */
export function outcomeSentence(outcome: ApplyOutcome): string | null {
  const unconfirmed = outcome.unconfirmedSent + outcome.unconfirmedUnknown;
  const clauses: string[] = [];

  if (outcome.confirmed > 0) {
    clauses.push(confirmedClause(outcome.confirmed));
  }
  if (unconfirmed > 0) {
    clauses.push(unconfirmedClause(unconfirmed));
  }
  if (outcome.notSent > 0) {
    clauses.push(notSentClause(outcome.notSent));
  }

  return clauses.length > 0 ? clauses.join(' ') : null;
}

/** True when more than one kind of ending happened, so nothing may stand for the whole. */
export function isMixed(outcome: ApplyOutcome): boolean {
  const kinds = [
    outcome.confirmed,
    outcome.unconfirmedSent + outcome.unconfirmedUnknown,
    outcome.notSent,
  ].filter((count) => count > 0);

  return kinds.length > 1;
}

/** The recovery instruction, said whenever anything could not be confirmed. */
export const CHECK_BEFORE_RETRYING = 'Basil could not confirm this change. Check the episode before trying again.';

/** Added ONLY when the send is known: a dispatched request whose reply was lost is not this. */
export const THE_CHANGE_WAS_SENT = 'The change was sent';

/** The same fact about more than one change. */
export const THE_CHANGES_WERE_SENT = 'The changes were sent';

/**
 * Whether the recovery view may say the changes reached the engine.
 *
 * Only when EVERY unconfirmed request is a known send. One request whose reply was lost makes the
 * whole claim untrue, and the safe reading of an untrue claim is to leave it out.
 */
export function sendIsKnown(outcome: ApplyOutcome): boolean {
  return outcome.unconfirmedSent > 0 && outcome.unconfirmedUnknown === 0;
}

/** "The change was sent." / "The changes were sent." — with a full stop, as a sentence. */
export function sentSentence(outcome: ApplyOutcome): string {
  return `${outcome.unconfirmedSent === 1 ? THE_CHANGE_WAS_SENT : THE_CHANGES_WERE_SENT}.`;
}

/**
 * T17-08: what the bar says BEFORE the first body is dispatched.
 *
 * The gate, the handshake, the storage check, the fresh read and the staleness guard all run before
 * anything leaves the browser, and a run can stop in any of them having sent nothing. "Applying"
 * through that interval claims a send that has not happened.
 */
export const PREPARING_CHANGES = 'Preparing changes...';

/** What the bar says while the bodies are going out. It is not success and does not look like it. */
export function applyingSentence(count: number): string {
  return `Applying ${changes(count)}...`;
}

/** What it says during VERIFICATION, after the send. Also not success. */
export function checkingSentence(count: number): string {
  return `Checking the ${count === 1 ? 'change' : 'changes'}...`;
}

/** The confirmation the writer answers before anything is sent at all. */
export function confirmQuestion(count: number): string {
  return `Apply ${changes(count)} to your episode? Basil will check the result. This will not publish it.`;
}

/** The two answers. Neither of them is the word "Apply" on its own. */
export const CONFIRM_APPLY = 'Apply changes';
export const CONFIRM_NOT_YET = 'Not yet';

/**
 * T18-01: the earlier uncertainty itself, said in one sentence wherever it has to be said.
 *
 * It is a fact about the EPISODE, so it is true of every later moment until something settles it:
 * while the confirmation is open, after the writer answers "Not yet", and beside the success
 * sentence of a later attempt that confirmed something else. One string, so the surface, the story
 * view's notice and the tests cannot drift apart.
 */
export const EARLIER_UNCONFIRMED = 'Earlier changes are still unconfirmed. '
  + 'Check the episode before trying again.';

/**
 * S-2: what the story view is told when the writer LEAVES with something unaccounted for.
 *
 * Reopening Basil restores the recovery block, so the uncertainty was never lost; what was lost was
 * any sign of it on the page the writer actually goes back to. Only a verified success emitted a
 * notice, and the story view keeps a notice until it is dismissed, so a writer who succeeded once
 * and then closed over an unresolved record was left looking at a green sentence saying their
 * change was applied and checked. This replaces it. It is a sentence about the EPISODE, so it says
 * what is true of the episode and what to do about it, and it stands until the record is settled.
 */
export const EXIT_UNCONFIRMED = 'Earlier changes could not be confirmed. '
  + 'Check the episode before applying again.';

/**
 * How the story view colours a notice Basil handed it (S-2).
 *
 * The mount renders one alert for everything Basil says, and it was always the success colour. A
 * notice that carries an unconfirmed change is not a success and must not be green, so the mount
 * asks here rather than deciding for itself, and the two sentences that carry the uncertainty are
 * named in one place.
 */
export function storyNoticeVariant(said: string | null | undefined): 'success' | 'warning' {
  const message = said || '';

  if (message.indexOf(EXIT_UNCONFIRMED) !== -1 || message.indexOf(EARLIER_UNCONFIRMED) !== -1) {
    return 'warning';
  }

  return 'success';
}

/**
 * S-3c: the browser would not keep the writer's attestation.
 *
 * It replaces the record-failed sentence that used to stand here, which was about a REQUEST and
 * said "nothing was sent". Nothing was being sent: the writer answered a question about an earlier
 * change, and the answer is what failed to save. The sentence now names what actually happened,
 * says what is still true of the episode, and offers the one action that exists, which is to press
 * the control again once the browser is behaving.
 */
export const ATTESTATION_NOT_SAVED = "Basil couldn't save your confirmation. "
  + 'Earlier changes are still unconfirmed. Try again.';

/**
 * T17-02: a new attempt that sent nothing, while an earlier one is still unsettled.
 *
 * The earlier attempt's uncertainty is a fact about the EPISODE, not about the attempt that
 * produced it, so it outlives that attempt. Replacing it with "nothing was sent" — true of the new
 * attempt and false of the episode — is how a writer is told everything is fine when it is not.
 */
export const NOTHING_NEW_EARLIER_UNCONFIRMED = `No new changes were sent on this attempt. ${EARLIER_UNCONFIRMED}`;

/** A plan as this module reads it: which request it is, and which node it was about. */
export interface PlanOfRecord {
  planId: string;
  targetBranchId: string | number;
}

/**
 * T18-01 — WHEN AN EARLIER UNCONFIRMED CHANGE COUNTS AS SETTLED.
 *
 * The default is that it is NOT settled: an unconfirmed change stays unconfirmed for the whole
 * session, through every cancelled confirmation and every later attempt, because nothing that
 * happens inside Basil can make the episode's own state known. Exactly two things settle it, and
 * they are the only two that are evidence:
 *
 * 1. **A fresh full read confirms that node.** A later attempt sent a change to the SAME node, the
 *    pipeline re-read the episode afterwards and the double-check verified it. The verification is
 *    a read of what the episode holds now, so the node's content is no longer in doubt — and this
 *    function clears that node, and only that node.
 * 2. **The writer says they checked it.** The surface offers one control on the warning that says
 *    so, and pressing it clears the whole set. That is the writer's own statement, not an
 *    inference, and it is the only thing a writer can do that settles anything.
 *
 * Everything else — opening the confirmation, answering "Not yet", a later attempt that confirmed
 * a DIFFERENT node, a later attempt that never sent anything, closing and reopening the panels —
 * leaves the set exactly as it was.
 */
export function settlementOf(
  state: ApplyState | null | undefined,
  plans: ReadonlyArray<PlanOfRecord>,
): { confirmed: string[]; unconfirmed: string[] } {
  const statuses = state ? state.statuses : {};
  const confirmed: string[] = [];
  const unconfirmed: string[] = [];

  plans.forEach((plan) => {
    const entry = statuses[plan.planId];

    if (!entry) {
      return;
    }
    const node = String(plan.targetBranchId);

    if (entry.status === 'verified') {
      confirmed.push(node);

      return;
    }
    // The same rule `outcomeOf` counts by: anything that left the browser, or may have left it,
    // and was not verified is a change nobody can account for.
    if (entry.delivery === 'sent' || entry.delivery === 'unknown') {
      unconfirmed.push(node);
    }
  });

  return { confirmed, unconfirmed };
}

/**
 * The unsettled nodes after one attempt resolves: what it confirmed leaves, what it could not
 * confirm joins, and everything the attempt said nothing about stays.
 */
export function nextUnsettled(
  current: ReadonlyArray<string>,
  state: ApplyState | null | undefined,
  plans: ReadonlyArray<PlanOfRecord>,
): string[] {
  const { confirmed, unconfirmed } = settlementOf(state, plans);
  const next = new Set<string>(current);

  confirmed.forEach((node) => next.delete(node));
  unconfirmed.forEach((node) => next.add(node));

  return Array.from(next);
}

/** What the recovery view is rendered from: the facts, and the sentence the pipeline filed. */
export interface RecoveryFacts {
  /** where every request of THIS attempt ended up, or null before any attempt has resolved. */
  outcome: ApplyOutcome | null;
  /** true when an earlier attempt in this session left changes unconfirmed. */
  earlierUnconfirmed: boolean;
  /** the pipeline's own sentence about this attempt, if it wrote one. */
  detail: string | null;
}

/**
 * The whole recovery message, rendered from the delivery and outcome facts (T17-01, T17-02).
 *
 * Every line is decided here, so there is no arrangement of state in which one paragraph claims a
 * send and another denies it:
 *
 * 1. the counts, with their actual states, never collapsed;
 * 2. "The change was sent." ONLY when every unconfirmed request proved receipt;
 * 3. the instruction, whenever anything is unconfirmed;
 * 4. the pipeline's own sentence about this attempt.
 *
 * An attempt that sent nothing while an earlier one is unsettled replaces the first three with the
 * one sentence that is true of both.
 *
 * T18-01 adds the two states this function used to have no answer for:
 *
 * * **no attempt has resolved since** (`outcome` is null) — the confirmation was opened and
 *   cancelled, or nothing has been tried yet. There is no attempt to report, and the earlier
 *   uncertainty is still the only thing there is to say;
 * * **this attempt confirmed something** — its own sentence stands, and the earlier uncertainty
 *   stands beside it. They are about different changes, so one can never be read for the other.
 */
export function recoveryLines(facts: RecoveryFacts): string[] {
  const { outcome, detail } = facts;
  const unconfirmed = outcome ? outcome.unconfirmedSent + outcome.unconfirmedUnknown : 0;
  const nothingSentNow = !outcome || (outcome.confirmed === 0 && unconfirmed === 0);
  const lines: string[] = [];

  if (facts.earlierUnconfirmed && nothingSentNow) {
    // No attempt has resolved since: there is no "this attempt" to say anything about.
    lines.push(outcome ? NOTHING_NEW_EARLIER_UNCONFIRMED : EARLIER_UNCONFIRMED);

    return detail ? lines.concat([detail]) : lines;
  }

  const said = outcome ? outcomeSentence(outcome) : null;

  if (said) {
    lines.push(said);
  }
  if (outcome && sendIsKnown(outcome)) {
    lines.push(sentSentence(outcome));
  }
  if (unconfirmed > 0) {
    lines.push(CHECK_BEFORE_RETRYING);
  }
  if (facts.earlierUnconfirmed) {
    // This attempt did something; the earlier changes are still unaccounted for. Both are said.
    lines.push(EARLIER_UNCONFIRMED);
  }
  if (detail) {
    // S-3c: the detail is the pipeline's own sentence about the last attempt, and it is shown for
    // exactly as long as it is true. A refusal that told the writer to press a control is resolved
    // the moment they press it, so the surface takes it off `applyFailure` on a settlement that
    // lands rather than leaving it here to outlive the control it names.
    lines.push(detail);
  }

  return lines;
}
